import React, { type ComponentProps, type ReactNode } from 'react'

import { ExtendedPaletteColorName } from '../../shared/MobiusColors'
import type { SemanticColorToken } from '../themes'
import { CssVars, NeutralColorToken, type ThemeColorToken } from '../themes'
import { IconButton } from './IconButton'
import { InternalModal } from './internal/InternalModal'
import type { Pictogram } from './pictograms'
import { Pog } from './Pog'
import { getColorValue, useTheme } from './ThemeProvider'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

type Props = {
  backgroundColor?: SemanticColorToken | ThemeColorToken
  description?: ReactNode
  firstButton: ReactNode
  secondButton?: ReactNode
  /**
   * Called when the user tries to close the alert sheet in any of the following ways:
   * clicking on a rendered `X` button, pressing the `Esc` key, or clicking
   * outside the alert sheet.
   *
   * If this function is not provided, the `X` will not be rendered and
   * the alert sheet will essentially be blocking -- you therefore *must* handle closing
   * the alert sheet via the first and/or second buttons.
   */
  onClose?: ComponentProps<typeof InternalModal>['onClose']
  pogProps?: {
    pictogram: Pictogram
    foregroundColorOverride?: ComponentProps<
      typeof Pog
    >['foregroundColorOverride']
    backgroundColorOverride?: ComponentProps<
      typeof Pog
    >['backgroundColorOverride']
  }
  foregroundColor?: SemanticColorToken | ThemeColorToken
  title: ReactNode
}

export function AlertSheet({
  backgroundColor: backgroundColorProp,
  description,
  firstButton,
  foregroundColor = NeutralColorToken.Fg1,
  onClose,
  pogProps,
  secondButton,
  title,
}: Props) {
  const id = useUniqueId()

  const theme = useTheme()
  const defaultBackgroundColor = theme.isDark
    ? NeutralColorToken.Bg1
    : ExtendedPaletteColorName.Mineral
  const backgroundColor = backgroundColorProp ?? defaultBackgroundColor

  return (
    <InternalModal
      aria-labelledby={id}
      onClose={onClose}
      overrideModalStyles={{
        background: getColorValue(backgroundColor, theme),
        padding: '24px',
      }}
      variant="wide"
    >
      {onClose && (
        <div
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <IconButton
            aria-label="Close"
            backgroundColor={backgroundColor}
            foregroundColor={foregroundColor}
            icon="Close24"
            onClick={onClose}
            type="button"
          />
        </div>
      )}
      {pogProps && (
        <Pog
          {...pogProps}
          overrideRootStyles={{
            margin: `0 auto calc(2 * ${CssVars.size.gutterWidth})`,
          }}
          size="hero"
        />
      )}
      <div
        css={{
          marginBottom: CssVars.size.gutterWidth,
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Type.Display color={foregroundColor} element="h1" size="S">
          <span id={id}>{title}</span>
        </Type.Display>
      </div>
      {description && (
        <div css={{ textAlign: 'center', width: '100%' }}>
          <Type.Text color={foregroundColor} size="S">
            {description}
          </Type.Text>
        </div>
      )}
      <div
        css={{
          marginTop: `calc(2 * ${CssVars.size.gutterWidth})`,
        }}
      >
        {firstButton}
      </div>
      {secondButton && (
        <div
          css={{
            marginTop: CssVars.size.gutterWidth,
          }}
        >
          {secondButton}
        </div>
      )}
    </InternalModal>
  )
}
